<template>
  <layout title="Terms and Conditions">
    <textFirstSection title="Terms and Conditions" />
    <div class="flex justify-center p-6">
      <div class="mt-6 html-editor max-w-6xl mb-6">
        <h5>LAST UPDATED: 23 DECEMBER 2025</h5>

        <p>
          PLEASE READ THIS TERMS OF USE CAREFULLY. BY ACCESSING OR USING OUR
          WEBSITES, MOBILE APPLICATIONS, SOFTWARE DEVELOPMENT KITS OR OTHER
          PRODUCTS OR SERVICES THAT HAVE LINKED TO THESE TERMS, YOU AGREE TO BE
          BOUND BY THESE TERMS AND ALL TERMS INCORPORATED BY REFERENCE. IF YOU
          DO NOT AGREE TO ALL OF THESE TERMS, DO NOT USE OUR WEBSITES, MOBILE
          APPLICATIONS OR OTHER PRODUCTS OR SERVICES.
        </p>

        <p>
          These Terms of Use (“<strong>Terms</strong>“) apply to your access to,
          and use of, the websites, mobile applications, software development
          kits and other products and services that have linked to these Terms
          (collectively, the “<strong>Services</strong>“) offered by YOONIK –
          AUTHENTICATION AND PRIVACY SYSTEMS, S.A. (referred in this agreement
          as either “<strong>we</strong>“, “<strong>us</strong>“,
          “<strong>our</strong>” or “<strong>Youverse</strong>”). These Terms do
          not alter in any way the terms or conditions of any other agreement
          you may have with Youverse for products, services or otherwise. If you
          are using the Services on behalf of any entity, you represent and
          warrant that you are authorized to accept these Terms on such entity’s
          behalf and that such entity agrees to be responsible to us if you
          violate these Terms.
        </p>

        <p>
          Youverse reserves the right to change or modify these Terms at any
          time and in our sole discretion. If Youverse makes changes to these
          Terms, we will provide notice of such changes, such as by providing
          notice through the Services, or by updating the “Last Updated” date at
          the top of these Terms. Your continued use of the Services will
          confirm your acceptance of the revised Terms. We encourage you to
          frequently review the Terms to ensure that you understand the terms
          and conditions that apply to your use of the Services. If you do not
          agree to any amended Terms, you must stop using the Services.
        </p>

        <p>
          In the event of conflict between this Agreement and any other written
          agreement between you and Youverse for the purchase of Services, the
          latter shall prevail.
        </p>

        <h2>Privacy</h2>

        <p>
          Please refer to our
          <inertia-link :href="$route('privacypolicy')"
            >Privacy Policy</inertia-link
          >
          for information about how Youverse collects, uses, and discloses
          information in relation to our identity and face verification
          services.
        </p>

        <h2>Eligibility, Registration and Account</h2>

        <p>
          The Services are not targeted towards, nor intended for use by, anyone
          under the age of 16. If you are under the age of 16, or age of legal
          majority under applicable law, you may only use the Services under the
          supervision of a parent or legal guardian who agrees to be bound by
          these Terms. By using the Services, you represent and warrant that you
          (a) are 16 years of age or older; (b) have not been previously
          suspended or removed from the Services; and (c) have full power and
          authority to enter into this agreement and that, in doing so, you will
          not violate any other agreement to which you are a party.
        </p>

        <p>
          In order to access and use certain areas or features of the Services,
          you may be required to register for an online account. When
          registering for an account, you cannot create an account name that
          incorporates a trademark without authorization from the trademark
          owner. We reserve the right to reclaim account names, or to take other
          reasonable action as necessary, on behalf of any business or
          individual that holds legal claim, including trademark rights, in a
          name.
        </p>

        <p>
          If you are an end user of our identity and face verification services,
          you understand and agree that Youverse will not be able to complete
          any identity and face verification services until you have provided
          all required information.
        </p>

        <p>
          In consideration of your use of the Services, you agree to (a) provide
          accurate, current and complete information; (b) maintain and promptly
          update your information; (c) accept all risks of unauthorized access
          to your account and the information you provide to Youverse; and (d)
          promptly notify Youverse if you discover or otherwise suspect any
          security breaches related to the Services.
        </p>

        <h2>Use of the Services</h2>
        <h3>Permitted Use</h3>
        <p>
          Subject to these Terms, Youverse grants you a limited, non-exclusive,
          non-transferable right to access and use the Services solely for their
          intended purposes, in accordance with applicable law, the applicable
          documentation, and these Terms. You agree to use the Services only in
          a lawful manner and in compliance with all applicable laws and
          regulations
        </p>

        <h3>Customer Obligations</h3>
        <p>
          You are responsible for ensuring that your use of the Services,
          including any use by your employees, contractors, or end users,
          complies with these Terms and applicable law. You shall implement
          appropriate technical and organizational measures to prevent
          unauthorized or unlawful use of the Services.
        </p>

        <h3>Prohibited Uses</h3>
        <p><b>You agree not to, and not to permit any third party to:</b></p>
        <p>
          (a) use the Services for any unlawful, illegal, or fraudulent purpose;
        </p>
        <p>
          (b) attempt to circumvent, disable, or interfere with security-related
          features of the Services;
        </p>
        <p>
          (c) misuse, share, or compromise access credentials or authentication
          mechanisms;
        </p>
        <p>(d) prohibited Discriminatory Use of AI Systems</p>

        <p>
          Use any artificial intelligence or AI-assisted system provided as part
          of the Services in a manner that results in unlawful discrimination,
          profiling, exclusion, or differential treatment of individuals or
          groups based on protected characteristics under applicable law,
          including but not limited to age, gender, sex, race, ethnicity,
          nationality, religion, disability, health status, sexual orientation,
          or any other characteristic protected by law. The AI-based
          functionalities of the Services are provided solely for the specific
          and documented purposes described in these Terms and applicable
          service documentation. You shall not use the Services to make fully
          automated decisions with legal or similarly significant effects on
          individuals, nor to engage in surveillance, monitoring, or other
          practices that infringe fundamental rights or applicable
          anti-discrimination laws. Any violation of this section constitutes a
          material breach of these Terms and may result in suspension or
          termination of access to the Services, without prejudice to any other
          rights or remedies available to Youverse.
        </p>

        <h2>Copyright and Limited License</h2>

        <p>
          Unless otherwise indicated on the Services or otherwise by Youverse,
          the Services and all content and other materials therein, including,
          without limitation, the Youverse logo and all designs, text, graphics,
          pictures, information, data, software, sound files, other files and
          the selection and arrangement thereof (collectively, “Content”) are
          the proprietary property of Youverse or our licensors are protected by
          international copyright laws.
        </p>

        <p>
          You are hereby granted a limited, non-exclusive, non-sublicensable
          license to access and use the Services and Content; however, such
          license is subject to these Terms and does not include (a) any resale
          or commercial use of the Services or the Content therein; (b) the
          collection and use of any product or service listings, pictures or
          descriptions; (c) the distribution, public performance or public
          display of any Content; (d) modifying or otherwise making any
          derivative uses of the Services or the Content, or any portion
          thereof; (e) use of any data mining, robots or similar data gathering
          or extraction methods; (f) downloading (other than the page caching)
          of any portion of the Services, the Content or any information
          contained therein, except as expressly permitted on the Services; and
          (g) any use of the Services or the Content other than for their
          intended purposes. Any use of the Services or the Content other than
          as specifically authorized herein, without the prior written
          permission of Youverse, is strictly prohibited and will terminate the
          license granted herein. Such unauthorized use may also violate
          applicable laws, including without limitation copyright and trademark
          laws and applicable communications regulations and statutes. Unless
          explicitly stated herein or otherwise by Youverse, nothing in these
          Terms shall be construed as conferring any license to intellectual
          property rights, whether by estoppel, implication or otherwise. This
          license is revocable at any time.
        </p>

        <p>
          Notwithstanding anything to the contrary in these Terms, the Services
          and Content may contain software components that are subject to
          separate license terms, in which case those license terms will apply
          to the access and use of such software components.
        </p>

        <h2>Trademarks</h2>

        <p>
          The Youverse logo and any other Youverse product or service names,
          logos or slogans that may appear on the Services are trademarks of
          Youverse, and may not be copied, imitated or used, in whole or in
          part, without the prior written permission of Youverse or the
          applicable trademark holder. You may not use any metatags or other
          “hidden text” utilizing “Youverse” or any other name, trademark or
          product or service name of Youverse without our prior written
          permission. In addition, the look and feel of the Services, including
          all page headers, custom graphics, button icons and scripts, is the
          service mark, trademark and/or trade dress of Youverse and may not be
          copied, imitated or used, in whole or in part, without our prior
          written permission. All other trademarks, registered trademarks,
          product names and company names or logos mentioned in the Services are
          the property of their respective owners. Reference to any products,
          services, processes or other information, by name, trademark,
          manufacturer, supplier or otherwise does not constitute or imply
          endorsement, sponsorship or recommendation by Youverse.
        </p>

        <h2>Hyperlinks</h2>

        <p>
          You are granted a limited, nonexclusive right to create a text
          hyperlink to the Services for noncommercial purposes, provided that
          such link does not portray Youverse or any of our products or services
          in a false, misleading, derogatory or otherwise defamatory manner and
          provided further that the linking site does not contain any adult or
          illegal material or any material that is offensive, harassing or
          otherwise objectionable. This limited right may be revoked at any
          time. You may not use a Youverse logo or other proprietary graphic of
          Youverse to link to the Services without the express written
          permission of Youverse. Further, you may not use, frame or utilize
          framing techniques to enclose any Youverse trademark, logo or other
          proprietary information, including the images found at the Services,
          the content of any text or the layout/design of any page or form
          contained on a page on the Services without Youverse’s express written
          consent. Except as noted above, you are not conveyed any right or
          license by implication, estoppel or otherwise in or under any patent,
          trademark, copyright or proprietary right of Youverse or any third
          party.
        </p>

        <p>
          Youverse makes no claim or representation regarding, and accepts no
          responsibility for, the quality, content, nature or reliability of
          third-party websites accessible by hyperlink from the Services, or
          websites linking to the Services. Such sites are not under the control
          of Youverse, and Youverse is not responsible for the contents of any
          linked site, any link contained in a linked site, or any review,
          changes or updates to such sites. Youverse provides these links to you
          only as a convenience, and the inclusion of any link does not imply
          affiliation, endorsement or adoption by Youverse of any site or any
          information contained therein. When you leave the Services, you should
          be aware that our terms and policies no longer govern. You should
          review the applicable terms and policies, including privacy and data
          gathering practices, of any site to which you navigate from the
          Services.
        </p>

        <h2>Third Party Content</h2>

        <p>
          Youverse may provide third party content on the Services and may
          provide links to web pages and content of third parties (collectively,
          “Third-Party Content”) as a service to those interested in this
          information. Youverse does not control, endorse or adopt any
          Third-Party Content and makes no representation or warranties of any
          kind regarding the Third-Party Content, including without limitation
          regarding its accuracy or completeness. You acknowledge and agree that
          Youverse is not responsible or liable in any manner for any
          Third-Party Content and undertakes no responsibility to update or
          review any Third-Party Content. Users use such Third-Party Content
          contained therein at their own risk.
        </p>

        <h2>
          Advertisements and Promotions; Third-Party Products, Services and
          Promotions
        </h2>

        <p>
          Youverse may display advertisements and promotions from third parties
          on the Services or may otherwise provide information about or links to
          third-party products or services on the Services. Your business
          dealings or correspondence with, or participation in promotions of,
          such third parties, and any terms, conditions, warranties or
          representations associated with such dealings or promotions, are
          solely between you and such third party. Youverse is not responsible
          or liable for any loss or damage of any sort incurred as the result of
          any such dealings or promotions or as the result of the presence of
          third-party information on the Services
        </p>

        <h2>Our Warranties and Disclaimers</h2>

        <p>
          We provide our Services using a commercially reasonable level of skill
          and care and we hope that you will enjoy using them. But there are
          certain things that we do not promise about our Services.
        </p>

        <p>
          While Youverse attempts to make your access to and use of the Services
          safe, Youverse cannot and does not represent or warrant that the sites
          or its server(s) are free of viruses or other harmful components;
          therefore, you should use industry-recognized software to detect and
          disinfect viruses from any download.
        </p>

        <p>
          Other than as expressly set out in these terms or additional terms,
          neither Youverse nor its suppliers or distributors makes any specific
          promises, either express or implied, about the Services. For example,
          we do not make any commitments about the Content within the Services,
          the specific functions of the Services or their reliability,
          availability or ability to meet your needs. Youverse is not
          responsible for typographical errors or omissions relating to pricing.
        </p>

        <p>
          Youverse provides the Services “as is”. To the extent permitted by
          law, we exclude all warranties.
        </p>

        <p>
          Youverse reserves the right to change any and all content contained in
          the Services and to modify, suspend or discontinue the Services or any
          features or functionality of the Services at any time without notice
          and without obligation or liability to you. Reference to any products,
          services, processes or other information, by trade name, trademark,
          manufacturer, supplier or otherwise does not constitute or imply
          endorsement, sponsorship or recommendation thereof, or any affiliation
          therewith, by Youverse. Some jurisdictions do not allow the disclaimer
          of implied terms in contracts with consumer, so some or all of the
          disclaimers in this section may not apply to you.
        </p>

        <h2>Limitation of Liability</h2>

        <p>
          In no event shall Youverse or its suppliers, distributors or
          affiliated parties be liable for any direct, special, indirect or
          consequential damages, or any other damages of any kind, including but
          not limited to loss of use, loss of profits or loss of data, whether
          in an action in contract, tort (including but not limited to
          negligence) or otherwise, arising out of or in any way connected with
          the use of or inability to use the Services or the content contained
          in or accessed through the Services, including without limitation any
          damages caused by or resulting from reliance by any user on any
          information obtained from Youverse, or that result from mistakes,
          omissions, interruptions, deletion of files or email, errors, defects,
          viruses, delays in operation or transmission or any failure of
          performance, whether or not resulting from acts of god, communications
          failure, theft, destruction or unauthorized access to Youverse’s
          records, programs or Services. In no event shall the aggregate
          liability of Youverse whether in contract, warranty, tort (including
          negligence, whether active, passive or imputed), product liability,
          strict liability or other theory, arising out of or relating to the
          use of or inability to use the Services or to these terms exceed the
          greater of 250 EUR or any compensation you pay, if any, to Youverse
          for access to or use of the Services.
        </p>

        <p>
          If you are using our Services on behalf of a business, that business
          accepts these terms. It will hold harmless and indemnify Youverse and
          its affiliates, officers, agents and employees from any claim, action
          or proceedings arising from or related to the use of the Services or
          violation of these terms, including any liability or expense arising
          from claims, losses, damages, judgements, litigation costs and legal
          fees.
        </p>

        <h2>Modifications to the Services</h2>

        <p>
          Youverse reserves the right to modify or discontinue, temporarily or
          permanently, the Services or any features or portions thereof without
          prior notice. You agree that Youverse will not be liable for any
          modification, suspension or discontinuance of the Services or any part
          thereof.
        </p>

        <h2>Termination</h2>

        <p>
          Notwithstanding any of these Terms, Youverse reserves the right,
          without notice and in our sole discretion, to terminate your right to
          use the Services, or any portion of thereof, and to block or prevent
          your future access to and use of the Services or any portion thereof.
        </p>

        <h2>Severability</h2>

        <p>
          If any provision of these Terms shall be deemed unlawful, void or for
          any reason unenforceable, then that provision shall be deemed
          severable from these Terms and shall not affect the validity and
          enforceability of any remaining provisions.
        </p>

        <h2>Governing Law</h2>

        <p>
          These Terms and all disputes and claims arising out of or in
          connection with them are governed by Portuguese law. With the sole
          exception of any application for injunctive relief, the parties
          irrevocably agree that the courts of Portugal have exclusive
          jurisdiction to settle any dispute or claim (whether contractual or
          non-contractual) arising out of or in connection with these Terms or
          the Services (including their subject matter or formation).
        </p>
      </div>
    </div>
  </layout>
</template>

<script>
const layout = () =>
  import(/* webpackChunkName: "layout" */ "../Components/Layout/layout.vue");
const textFirstSection = () =>
  import(
    /* webpackChunkName: "textFirstSection" */ "../Components/Layout/textFirstSection.vue"
  );
export default {
  components: { layout, textFirstSection },
};
</script>

<style>
@import "../../css/custom.css";
</style>